import numeral from 'numeral';

import type { DeltaDisplayMode } from '../report-configurations/reportConfigurationsTypes';
import type { CategoryComparisonRow } from './comparisonBarTypes';
import type {
  ComparisonDeltaDirection as ComparisonBarDeltaDirection,
  ComparisonDeltaSentimentValue as ComparisonDeltaSentiment,
} from './comparisonDeltaDisplay';
import { comparisonDeltaDirection, resolveComparisonDeltaPresentation } from './comparisonDeltaDisplay';

// The rules themselves live in `comparisonDeltaDisplay`, shared with the comparison grid's $ Change
// cell so the chip and the cell beside it can never disagree. What is here is the projection from
// this chart's row shape onto them, plus the text formatters.

/**
 * Everything these formatters read: the five delta fields and the direction they are measured in.
 *
 * Structural rather than `CategoryComparisonRow`, because the chart header runs the identical rules
 * over the *report's* totals, which have a delta but no id, name, colour or bars. Restating the
 * formatting there would let the header and the rows beneath it disagree about the same movement.
 */
type ComparisonDeltaPeriodValue = number | null | { value: number | null };

export type ComparisonDeltaSource = Pick<
  CategoryComparisonRow,
  'expectedSign' | 'deltaAmount' | 'deltaAmountSigned' | 'deltaPercent'
> & {
  values: ComparisonDeltaPeriodValue[];
};

/** Nothing to compare. An em dash rather than a zero, which would claim the row held steady. */
const NO_DELTA = '—';

/*
 * The two zero-base states — `new_spend` (nothing in the compared range) and `no_spend_yet`
 * (nothing in the current one) — used to render as the spending dashboard's named chips rather
 * than as a movement. They no longer do (SIMPL-35534): the wording was the dashboard's, not this
 * report's, and it read as nonsense on an income chart, which does not measure spend at all. Both
 * are formatted as an ordinary delta below, which is what the comparison spec asks for.
 *
 * `deltaKind` still distinguishes them, because the *model* needs to — a reported zero is what
 * makes the ratio `null` for `new_spend` and exactly 100 for `no_spend_yet`. Only the presentation
 * was wrong, and those two values already carry everything the formatters need.
 */

/**
 * Display precision, because it changes what counts as a movement at all.
 *
 * `hideCents` is not only a formatting concern: a delta of $0.40 formats as `$0` with cents hidden,
 * and an arrow or a `+` beside a zero contradicts the number it is attached to. The shared helpers
 * all take `hideCents` for exactly this reason, so every one of them has to be handed it.
 */
export type ComparisonDeltaDisplay = {
  deltaDisplayMode?: DeltaDisplayMode;
  /** @deprecated Use `deltaDisplayMode`. Retained for public API compatibility. */
  showExpensesAsNegative?: boolean;
  hideCents: boolean;
};

const resolveDisplayMode = (display: ComparisonDeltaDisplay): DeltaDisplayMode =>
  display.deltaDisplayMode ?? (display.showExpensesAsNegative ? 'SIGN' : 'ARROW');

const periodValue = (value: ComparisonDeltaPeriodValue | undefined): number | null => {
  if (value == null) return null;
  return typeof value === 'number' ? value : value.value;
};

/** Direction derived from period values, never from the server delta's display sign. */
const directionFor = (row: ComparisonDeltaSource, hideCents: boolean): ComparisonBarDeltaDirection => {
  const current = periodValue(row.values[0]);
  const compared = periodValue(row.values[1]);
  if (current == null && compared == null) return null;

  return comparisonDeltaDirection((current ?? 0) - (compared ?? 0), row.expectedSign, hideCents);
};

export type ComparisonDeltaFormatters = {
  /** Amount → money string, cents already decided by `hideCents`. */
  formatMoney: (value: number) => string;
  privacyMask: (value: string) => string;
};

export type ComparisonDeltaSpokenSelection = {
  showAmount: boolean;
  showPercentage: boolean;
};

const DEFAULT_SPOKEN_SELECTION: ComparisonDeltaSpokenSelection = {
  showAmount: true,
  showPercentage: true,
};

const formatDeltaAmount = (
  row: ComparisonDeltaSource,
  sign: '' | '+' | '-',
  { formatMoney, privacyMask }: ComparisonDeltaFormatters,
): string => {
  const value = row.deltaAmountSigned ?? row.deltaAmount;
  return value == null ? NO_DELTA : `${sign}${privacyMask(formatMoney(Math.abs(value)))}`;
};

/**
 * The chip's amount text.
 *
 * The underlying value keeps QCS's arithmetic sign. Arrow mode hides that sign because direction
 * is rendered separately; signed mode prefixes the same authoritative value explicitly.
 */
export const deltaAmountText = (
  row: ComparisonDeltaSource,
  display: ComparisonDeltaDisplay,
  { formatMoney, privacyMask }: ComparisonDeltaFormatters,
): string => {
  const { sign } = resolveComparisonDeltaPresentation(
    directionFor(row, display.hideCents),
    row.deltaAmountSigned,
    row.expectedSign,
    resolveDisplayMode(display),
    display.hideCents,
  );
  return formatDeltaAmount(row, sign, { formatMoney, privacyMask });
};

/**
 * The percent shown beside the chip, always to two decimals to match the design and the grid.
 *
 * Never privacy-masked: a ratio discloses no balance, so hiding it would cost information for no
 * privacy. `new_spend` divides by a zero base and so has no ratio at all — the model hands it a
 * `null` percent, which takes the em dash below. `no_spend_yet` does have one, exactly 100%, and
 * shows it.
 */
export const deltaPercentText = (row: ComparisonDeltaSource, display: ComparisonDeltaDisplay): string => {
  if (row.deltaPercent == null) return NO_DELTA;
  const deltaDisplayMode = resolveDisplayMode(display);
  const value = deltaDisplayMode === 'SIGN' ? row.deltaPercent : Math.abs(row.deltaPercent);
  const displayedValue = Math.abs(value) < 0.005 ? 0 : value;
  const sign = deltaDisplayMode === 'SIGN' && displayedValue > 0 ? '+' : '';
  return `${sign}${numeral(displayedValue).format('0,0.00')}%`;
};

/**
 * The delta as one spoken phrase, for the screen-reader label on a row's drill-down control.
 *
 * The visual column conveys direction with an arrow glyph and colour, neither of which survives
 * being read aloud, so the direction is restated as a word here.
 */
export const deltaSpokenText = (
  row: ComparisonDeltaSource,
  display: ComparisonDeltaDisplay,
  formatters: ComparisonDeltaFormatters,
  selection: ComparisonDeltaSpokenSelection = DEFAULT_SPOKEN_SELECTION,
): string => deltaPresentationFor(row, display, formatters, selection).spokenText;

/** One resolved value for every visual and spoken part of a chart delta. */
export const deltaPresentationFor = (
  row: ComparisonDeltaSource,
  display: ComparisonDeltaDisplay,
  formatters: ComparisonDeltaFormatters,
  spokenSelection: ComparisonDeltaSpokenSelection = DEFAULT_SPOKEN_SELECTION,
): {
  amountText: string;
  percentText: string;
  direction: ComparisonBarDeltaDirection;
  sentiment: ComparisonDeltaSentiment;
  spokenText: string;
} => {
  const presentation = resolveComparisonDeltaPresentation(
    directionFor(row, display.hideCents),
    row.deltaAmountSigned,
    row.expectedSign,
    resolveDisplayMode(display),
    display.hideCents,
  );

  const amountText = formatDeltaAmount(row, presentation.sign, formatters);
  const percentText = deltaPercentText(row, display);
  let spokenText = '';
  if (spokenSelection.showAmount || spokenSelection.showPercentage) {
    spokenText = 'no comparison';
    if (row.deltaAmountSigned != null) {
      const selectedValues = [
        spokenSelection.showAmount && amountText !== NO_DELTA ? amountText : '',
        spokenSelection.showPercentage && percentText !== NO_DELTA ? percentText : '',
      ].filter(Boolean);

      if (presentation.direction) {
        spokenText = selectedValues.length ? `${presentation.direction} ${selectedValues.join(', ')}` : 'no comparison';
      } else {
        const selectedPercent = spokenSelection.showPercentage && percentText !== NO_DELTA ? percentText : '';
        spokenText = selectedPercent ? `no change, ${selectedPercent}` : 'no change';
      }
    }
  }

  return {
    amountText,
    percentText,
    direction: presentation.arrow,
    sentiment: presentation.sentiment,
    spokenText,
  };
};
